import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { throttle } from '../../../utils/base'

/**
 * Wrapper for the `<iframe />` element. This adds support for writing an html
 * string to be the content of the iframe. Also adds support for injected styles.
 *
 * This component is only really meant for use with the `content` prop, if the `src`
 * is going to be external, it's probably a better idea to just use a regular iframe.
 *
 * @todo write tests for this.
 * @todo add better documentation and a story for this.
 */
class Iframe extends Component {
  /**
   * Component constructor
   */
  constructor() {
    super()

    this.state = {
      height: 0,
      loaded: false,
    }

    this.setRef = this.setRef.bind(this)
    this.setupFonts = this.setupFonts.bind(this)
    this.setup = this.setup.bind(this)
    this.watchHeightChanges = this.watchHeightChanges.bind(this)
    this.heightChangeListener = this.heightChangeListener.bind(this)
    this.throttledHeightListener = throttle(this.heightChangeListener.bind(this), 200)
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.decodeDoubleEscapedHTML = this.decodeDoubleEscapedHTML.bind(this)
    this.decodeParam = this.decodeParam.bind(this)
    this.getFragmentId = this.getFragmentId.bind(this)
    this.applyHighlight = this.applyHighlight.bind(this)
  }

  /**
   * Decodes a URL parameter that has been encoded.
   * This is mainly used for decoding the hash fragment in the URL.
   * and anchor tags.
   */
  decodeParam(encodedUrlParam) {
    try {
      const urlDecoded = decodeURIComponent(encodedUrlParam)
      const base64Decoded = atob(urlDecoded)
      return base64Decoded
    } catch (e) {
      return ''
    }
  }

  /**
   * Gets the fragment id from the URL.
   */
  getFragmentId() {
    const params = new URLSearchParams(window.location.search)
    const encodedSrc = params.get('src')
    if (encodedSrc) {
      const decodedUrl = this.decodeParam(encodedSrc)
      const fragmentMatch = decodedUrl.match(/#(.+)$/)
      return fragmentMatch ? fragmentMatch[1] : null
    }
    return null
  }

  /**
   * Helper function that applies a highlight to an element.
   * The highlight is then removed after several seconds.
   */
  applyHighlight(elem) {
    elem.scrollIntoView({ behavior: 'smooth', block: 'center' })
    elem.style.transition = 'background-color 0.5s ease'
    elem.style.backgroundColor = '#78E1F9'
    setTimeout(() => {
      elem.style.backgroundColor = ''
    }, 5000)
  }

  /**
   * Initializes stuff when the component is first mounted.
   */
  componentDidMount() {
    if (this.props.content) {
      this.setup()
    }
  }

  /**
   * Resets iframe if the right stuff has changed.
   *
   * @param {object} prevProps - Previous component props
   */
  componentDidUpdate(prevProps) {
    const { content, innerCSS, readerOptions } = this.props
    // Only run the setup if the iframe's contents has changed.
    // @question is there ever a time we will want to set the content to `''`?
    // we don't currently support that.
    if ((content && prevProps.content !== content) || readerOptions !== prevProps.readerOptions) {
      this.setup()
    }

    // Update styles if `innerCSS` changes
    if (innerCSS !== prevProps.innerCSS) {
      this.updateStyles()
    }
  }

  // Clean up double-escaped HTML entities
  decodeDoubleEscapedHTML(htmlString) {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = htmlString
    const decoded = textarea.value

    return decoded
  }

  /**
   * Stores the iframe's ref and document elements to instance variables. If
   * an `innerRef` prop is set, then the iframe is passed there so the parent
   * can also have access to the iframe for whatever reason.
   *
   * @param {element} iframe - iframe element
   */
  setRef(iframe) {
    if (!iframe) {
      return
    }

    this.iframe = iframe
    this.iframeDocument = (this.iframe.contentWindow || this.iframe.contentDocument).document

    // Send the iframe up to the parent if needed.
    if (this.props.innerRef) {
      this.props.innerRef(iframe)
    }
  }

  setupFonts() {
    if (!this.iframeDocument) {
      return
    }
    const fontLinks = [
      'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap',
      'https://fonts.cdnfonts.com/css/palatino',
      'https://fonts.cdnfonts.com/css/opendyslexic',
      'https://fonts.cdnfonts.com/css/baskerville',
    ]

    fontLinks.forEach((href) => {
      const fontLink = this.iframeDocument.createElement('link')
      fontLink.href = href
      fontLink.rel = 'stylesheet'
      this.iframeDocument.head.appendChild(fontLink)
    })
  }

  /**
   * Mouse move handler to show/hide toolbars.
   */
  handleMouseMove() {
    // If not near edges, start a timer to hide the toolbars after 5 seconds.
    if (this.toolbarTimeout) {
      clearTimeout(this.toolbarTimeout)
    }
    this.toolbarTimeout = setTimeout(() => {
      this.props.onClick(false)
    }, 5000)
  }

  /**
   * Writes content to the iframe and injects custom style sheets. Once the
   * iframe's contents have been updated, it calls the `onSetup` callback if
   * one is given.
   */
  setup() {
    const { content, onSetup, onClick } = this.props

    if (!this.iframeDocument) {
      return
    }

    const cleanedContent = this.decodeDoubleEscapedHTML(content)

    // write the contents to the iframe
    this.iframeDocument.open()
    this.iframeDocument.write(cleanedContent)
    this.iframeDocument.close()

    // add style container
    const styleContainer = this.iframeDocument.createElement('style')

    styleContainer.type = 'text/css'
    this.iframeDocument.head.appendChild(styleContainer)
    this.sheet = styleContainer.sheet
    this.updateStyles()
    this.setupFonts()

    this.watchHeightChanges()

    if (typeof onClick === 'function') {
      this.iframeDocument.body.addEventListener('click', onClick)
    }

    this.iframeDocument.addEventListener('mousemove', () => {
      this.handleMouseMove()
    })

    // trigger the callback prop if one is present
    onSetup(this.iframe, this.iframeDocument)

    // After content loads, check for a fragment id and highlight it
    const fragmentId = this.getFragmentId()
    if (fragmentId) {
      const target = this.iframeDocument.getElementById(fragmentId)
      if (target) {
        // Anchor tags usually have a parent <p /> or <div /> tag. The anchors themselves have no content, or will not highlight correctly.
        const targetParagraph = target.closest('p')
        const targetDiv = target.closest('div')
        if (targetParagraph) {
          this.applyHighlight(targetParagraph)
        } else if (targetDiv) {
          this.applyHighlight(targetDiv)
        }
      }
    }
  }

  /**
   * We need to keep the iframe's height matching the width of its content so a
   * scroll bar isn't needed. So we add event listeners to all the different
   * ways we can think of the content of the Iframe changing height, and then
   * adjust as needed.
   */
  watchHeightChanges() {
    // Set the initial height.
    this.setState({ height: this.iframeDocument.body.clientHeight })

    // Watch any changes in static assets like images and css.
    const resources = this.iframeDocument.querySelectorAll('link, img')

    Array.from(resources).map((resource) => {
      resource.addEventListener('load', this.throttledHeightListener, false)

      return resource
    })

    // Watch resizing of the window.
    window.addEventListener('resize', this.throttledHeightListener, false)
  }

  /**
   * Callback for when a change in height might've been detected. Compares the
   * previous known height of the content to the current height, if it's different
   * then we update state so we know how to re-render the contents.
   */
  heightChangeListener() {
    if (!this.iframeDocument) {
      return
    }

    if (this.state.height !== this.iframeDocument.body.clientHeight) {
      this.setState({ height: this.iframeDocument.body.clientHeight })

      if (!this.state.loaded) {
        this.setState({ loaded: true })
      }
    }
  }

  /**
   * Converts the `innerCSS` prop to css rules and then injects them into the
   * iframe.
   *
   * @todo remove existing rules from the stylesheet so this can support updating
   * styles dynamically.
   */
  updateStyles() {
    const { innerCSS } = this.props

    if (!this.sheet) {
      return
    }

    // Clear all existing rules from the stylesheet
    while (this.sheet.cssRules.length > 0) {
      this.sheet.deleteRule(0)
    }

    // Insert new rules
    Object.keys(innerCSS).forEach((selector) => {
      const selectorStyles = Object.keys(innerCSS[selector]).reduce((styles, rule) => {
        // Convert camelCase to kebab-case
        const ruleSelector = rule.replace(/([a-z])([A-Z])/g, (_, a, b) => `${a}-${b.toLowerCase()}`)
        return `${styles} ${ruleSelector}: ${innerCSS[selector][rule]};`
      }, '')

      this.sheet.insertRule(`${selector} { ${selectorStyles} }`)
    })
  }

  /**
   * Figures out how to render the component.
   *
   * @returns {function} Component
   */
  render() {
    const { innerCSS, innerRef, onSetup, title, style, content, ...rest } = this.props
    const { height, loaded } = this.state

    const styles = {
      ...style,
      height: `${height}px`,
      width: '100%',
      visibility: loaded ? 'visible' : 'hidden',
    }

    return <iframe ref={this.setRef} title={title} style={styles} {...rest} />
  }
}

Iframe.defaultProps = {
  content: '',
  innerCSS: {},
  innerRef: () => {},
  onSetup: () => {},
  style: {},
}

Iframe.propTypes = {
  /** html string of contents to display inside iframe */
  content: PropTypes.string,
  /** JSS styling to inject into the iframe's content */
  innerCSS: PropTypes.shape({}),
  /** function to access the `ref` of the iframe */
  innerRef: PropTypes.func,
  /** callback for when the iframe's content has been set */
  onSetup: PropTypes.func,
  /** Title of iframe. <iframe> elements must have a unique title property */
  title: PropTypes.string.isRequired,
  /** Element style prop. */
  style: PropTypes.shape({}),
}

export default Iframe
